import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Pagination } from '@libComponents';
import DialogWrapper from '@libComponents/Dialog';
import FilterInput from '@libComponents/FilterInput';

import { getUsersWithRoles, manageRole } from 'roles/redux.actions';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import {
    Autocomplete,
    Avatar,
    Box,
    Chip,
    CircularProgress,
    IconButton,
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { ROWS_PER_PAGE } from 'roles/common';

const propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    roles: PropTypes.arrayOf(PropTypes.string).isRequired
};

// MUI string avatar
function stringAvatar(name) {
    const nameParts = name.split(' ').filter((part) => part.length > 0);
    return {
        sx: {
            color: '#ffffff',
            width: 32,
            height: 32,
            fontSize: 14,
            bgcolor: (theme) => theme.palette.primary.main
        },
        children:
            nameParts.length >= 2
                ? `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
                : nameParts.length === 1
                ? nameParts[0][0].toUpperCase()
                : '?'
    };
}

const RoleChip = styled(Chip)(({ theme }) => ({
    margin: theme.spacing(0.25),
    height: 24,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    
    '& .MuiChip-label': {
        fontSize: '0.75rem',
        paddingLeft: 8,
        paddingRight: 4,
        fontWeight: 500,
        textTransform: 'capitalize'
    },
    '& .MuiChip-deleteIcon': {
        fontSize: '1rem',
        width: 20,
        height: 20,
        margin: 0,
        padding: 2,
        marginRight: 3,
        borderRadius: '50%',
        color: theme.palette.grey,
        backgroundColor: theme.palette.background.paper,
        '&:hover': {
            color: theme.palette.error.main,
            backgroundColor: theme.palette.background.paper,
        }
    }
}));

const ManageUsersModal = ({ open, onClose, roles }) => {
    const dispatch = useDispatch();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [pageSize, setPageSize] = useState(ROWS_PER_PAGE);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [updatingUser, setUpdatingUser] = useState(null);

    const tableHeaders = ['User', 'Email', 'Assigned Roles', 'Actions'];

    /**
     * Wraps the saga dispatch + handleComplete callback in a Promise so the
     * component can still await results for local UI state updates.
     */
    const fetchUsers = useCallback(
        ({ offset = '', limit, roleName, search } = {}) => {
            setLoading(true);
            return new Promise((resolve) => {
                dispatch(
                    getUsersWithRoles({
                        limit: limit ?? pageSize,
                        offset,
                        roleName: roleName || undefined,
                        search: search || undefined,
                        handleComplete: (error, result) => {
                            if (error) {
                                console.error('Error fetching users with roles:', error?.response?.data || error);
                                setUsers([]);
                                setTotalRows(0);
                            } else {
                                setUsers(result.data);
                                setTotalRows(result.total);
                            }
                            setLoading(false);
                            resolve();
                        },
                    })
                );
            });
        },
        [dispatch, pageSize]
    );

    useEffect(() => {
        if (open) {
            fetchUsers({ offset: '', roleName: roleFilter, search: searchTerm });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleSearch = (value) => {
        setSearchTerm(value);
        setCurrentPage(1);
        fetchUsers({ offset: '', roleName: roleFilter, search: value });
    };

    const handleRoleFilterChange = (value) => {
        setRoleFilter(value);
        setCurrentPage(1);
        fetchUsers({ offset: '', roleName: value, search: searchTerm });
    };

    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage);
        fetchUsers({
            offset: (newPage - 1) * pageSize,
            limit: pageSize,
            roleName: roleFilter,
            search: searchTerm
        });
    };

    const handleRowsPerPageChange = (newPageSize) => {
        setPageSize(newPageSize);
        setCurrentPage(1);
        fetchUsers({
            offset: 0,
            limit: newPageSize,
            roleName: roleFilter,
            search: searchTerm
        });
    };

    const handleManageRole = (email, roleName, action) => {
        setUpdatingUser(email);
        dispatch(
            manageRole({
                email,
                roleName,
                action,
                handleComplete: (error) => {
                    if (!error) {
                        // Refresh the list to reflect the updated roles
                        fetchUsers({
                            offset: (currentPage - 1) * pageSize,
                            limit: pageSize,
                            roleName: roleFilter,
                            search: searchTerm,
                        });
                    }
                    setUpdatingUser(null);
                },
            })
        );
    };

    const getFullName = (user) => {
        const parts = [user.firstname, user.middlename, user.lastname].filter(Boolean);
        return parts.length > 0 ? parts.join(' ') : user.email;
    };

    return (
        <DialogWrapper
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            dialogPadding={0}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 400, backgroundColor: (theme) => theme.palette.background.white, p: { xs: 1.5, sm: 2 } }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 1, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Manage Users
                    </Typography>
                    <IconButton onClick={onClose} size="medium" aria-label="close" sx={{ color: (theme) => theme.palette.grey[500] }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box sx={{ width: { xs: '100%', sm: 250 } }}>
                        <FilterInput
                            name="manage-users-search"
                            handleSearch={handleSearch}
                            value={searchTerm}
                            placeholder="Search by name or email..."
                        />
                    </Box>
                    <Autocomplete
                        size="small"
                        sx={{ width: { xs: '100%', sm: 250 }, height: 36, '& .MuiInputBase-root': { fontSize: '12px' }, '& .MuiInputLabel-root': { fontSize: '12px' } }}
                        options={roles}
                        getOptionLabel={(option) => option || ''}
                        value={roleFilter || null}
                        onChange={(event, newValue) => {
                            handleRoleFilterChange(newValue || '');
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Filter by Role"
                                placeholder="Select role"
                                sx={{height: 36, '& .MuiInputBase-root': { height: 36 } }}
                            />
                        )}
                    />
                </Box>

                {/* Table */}
                <Box sx={{ position: 'relative', overflowX: 'auto', overflowY: 'hidden' }}>
                    {loading && users.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {loading && (
                                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10 }}>
                                    <CircularProgress />
                                </Box>
                            )}
                            <TableContainer component={Box} sx={{ boxShadow: 'none', minWidth: { xs: 760, sm: 'auto' } }}>
                            <Table>
                            <TableHead>
                                <TableRow>
                                    {tableHeaders.map((th, i) => (
                                        <TableCell
                                            key={th}
                                            sx={{
                                                pl: i === 0 ? 4 : 2,
                                                pr: i === tableHeaders.length - 1 ? 4 : 2,
                                                fontWeight: 'bold',
                                                fontSize: '0.75rem',
                                                textTransform: 'uppercase',
                                                color: 'text.secondary',
                                                borderBottom: 2,
                                                borderColor: 'divider'
                                            }}
                                        >
                                            {th}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 8 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No users found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map((user) => {
                                        const fullName = getFullName(user);
                                        return (
                                            <TableRow key={user.email} hover>
                                                <TableCell sx={{ pl: 4 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <Avatar {...stringAvatar(fullName)} />
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {fullName}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ pl: 2 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {user.email}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ pl: 2 }}>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {user.roles && user.roles.length > 0 ? (
                                                            user.roles.map((role) => (
                                                                <RoleChip
                                                                    key={role}
                                                                    label={role}
                                                                    size="medium"
                                                                    onDelete={
                                                                        updatingUser === user.email
                                                                            ? undefined
                                                                            : () => handleManageRole(user.email, role, 'remove')
                                                                    }
                                                                    deleteIcon={
                                                                        updatingUser === user.email ? (
                                                                            <CircularProgress size={14} />
                                                                        ) : (
                                                                            <CloseIcon fontSize="small" />
                                                                        )
                                                                    }
                                                                />
                                                            ))
                                                        ) : (
                                                            <Typography variant="caption" color="text.disabled">
                                                                No roles assigned
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ pr: 4, textAlign: 'left' }}>
                                                    <Autocomplete
                                                        size="small"
                                                        sx={{ minWidth: 130, '& .MuiInputBase-root': { fontSize: '12px' }, '& .MuiInputLabel-root': { fontSize: '12px', } }}
                                                        options={roles.filter((role) => !user.roles || !user.roles.includes(role))}
                                                        getOptionLabel={(option) => option || ''}
                                                        value={null}
                                                        onChange={(event, newValue) => {
                                                            if (newValue) {
                                                                handleManageRole(user.email, newValue, 'add');
                                                            }
                                                        }}
                                                        disabled={updatingUser === user.email}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                placeholder="Add Role"
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    startAdornment: <AddIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary', }} />,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                        </TableContainer>
                        </>
                    )}
                </Box>

                {/* Pagination */}
                {totalRows > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', borderTop: 1, borderColor: 'divider', pt: 1, overflowX: 'auto' }}>
                        <Pagination
                            count={Math.ceil(totalRows / pageSize)}
                            onChange={handleChangePage}
                            page={currentPage}
                            sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
                            isRowsPerPageVarient={true}
                            rowsPerPageProps={{
                                name: 'manage-users-rows',
                                onChange: handleRowsPerPageChange,
                                value: pageSize
                            }}
                            rowsPerPageOptions={[
                                { label: '10', value: 10 },
                                { label: '25', value: 25 },
                                { label: '50', value: 50 }
                            ]}
                        />
                    </Box>
                )}
            </Box>
        </DialogWrapper>
    );
};

ManageUsersModal.propTypes = propTypes;

export default ManageUsersModal;
